import { StoryNode } from "imagica-corekit/dist/base/storyV2/domain/StoryNode";
import { useHomePreviewMethods } from "../useHomePreviewMethods";
import { isArray, isPlainObject } from "lodash";
import { CreatorNodesStore } from "../store/CreatorNodesStore";
import { getIt } from "@uikit/getIt";
import { CreatorSaasAppStore } from "../store/CreatorSaasAppStore";
import { PreviewContentUtil } from "@uiview/views/PreviewApp/PreviewContentUtil";
import { HomePluginStore } from "imagica-corekit/dist/base/store/HomePluginStore";

const creatorSaasAppStore = getIt(CreatorSaasAppStore);
const creatorNodesStore = getIt(CreatorNodesStore);
const homePluginStore = getIt(HomePluginStore);

let instance: ShareAppManager;
/**
 * 内部实现，为UI组件使用的 hook
 * @returns
 */
function useShareAppManager(): ShareAppManager {
  const homePreviewMethods = useHomePreviewMethods();

  if (!instance) {
    return new ShareAppManager(homePreviewMethods);
  }

  // 刷新方法
  instance.homePreviewMethods = homePreviewMethods;

  return instance;
}

type HomePreviewMethods = ReturnType<typeof useHomePreviewMethods>;
type TransformData<T = any> = { id: string; type: string; data: T };
export type RunAllUpdateDataType = { updateNode?: StoryNode; transformData: TransformData | TransformData[] };

/**
 * 由于homeV1 reactflow 和 shareapp 是硬绑定，两个组件会同时存在
 *
 * homeV2 中，两个组件已经分离，则会丢失 reactflow 中，子组件用来监听或改变preview 数据的逻辑(比如 useEffect)
 *
 * 该实现类为了弥补其中丢失的逻辑，所以会重新实现一份
 */
export class ShareAppManager {
  static use = useShareAppManager;
  constructor(public homePreviewMethods: HomePreviewMethods) {}

  runAllUpdateData?: RunAllUpdateDataType;

  /**
   * 设置 runall 后的数据
   * @param runAllUpdateData
   */
  setRunAllUpdateData = (runAllUpdateData: RunAllUpdateDataType): void => {
    this.runAllUpdateData = runAllUpdateData;

    // trigger update to preview
    this.effectTextAreaValue();
  };

  private handleUpdatePreviewData(transformData: any): void {
    if (transformData.type !== "customNode") return;
    let text = transformData.data.textAreaValue;
    if (typeof text === "string") {
      text = transformData.data.textAreaValue.trim();
    }
    this.homePreviewMethods.updatePreviewNodeContent({
      nodeId: transformData.id,
      textAreaValue: text,
      displayType: transformData.data.displayType,
      chatBoxParam: transformData.data.chatBoxParam,
      errorText: transformData.data.errorText,
      data: transformData.data,
    });
  }
  /**
   * @target ShareAppManager#1,ShareAppManager#2
   * @returns
   */
  effectTextAreaValue(): void {
    if (!this.runAllUpdateData) {
      console.warn("ShareAppManager runAllUpdateData undefined");
      return;
    }

    const { transformData, updateNode } = this.runAllUpdateData;

    const nodeIds = creatorSaasAppStore.state.saasUIData.output.map((x: any) => x?.results?.[0]?.nodeId);
    const refreshNodeIdx = nodeIds.findIndex(
      (x: any) => !creatorNodesStore.state.nodes.map((j: any) => j.id).includes(x)
    );
    const homePluginState = homePluginStore.state;

    // console.log("refreshNode",nodeIds,refreshNodeIdx)
    if (refreshNodeIdx !== -1) {
      let list: any[] = [];
      if (Array.isArray(transformData)) {
        list = PreviewContentUtil.getPreviewOutputByGroupNode({
          output: creatorSaasAppStore.state.saasUIData.output,
          groupChildeNodes: (transformData as any).filter((x: any) => x.type === "customNode"),
          getResult: false,
          addContentId: "",
          isShare: homePluginState.isShare,
        });
      } else {
        list = PreviewContentUtil.getPreviewOutputBySingleNode({
          saasUIData: creatorSaasAppStore.state.saasUIData,
          output: creatorSaasAppStore.state.saasUIData.output,
          addContentId: "",
          node: transformData as any,
          isShare: homePluginState.isShare,
        });
      }

      let children: string[] = [];
      if (updateNode && updateNode.options.id === nodeIds[refreshNodeIdx]) {
        children = [...(updateNode?.options.children ?? {})];
      }
      if (children) {
        const output = [...creatorSaasAppStore.state.saasUIData.output];
        children.forEach(x => {
          const obj = list.find(j => j.results?.[0]?.nodeId === x);
          console.log("forEach", list, x, obj);
          if (obj) {
            output.splice(refreshNodeIdx, 1, obj);
          }
        });

        creatorSaasAppStore.setSaasUIData(prev => {
          return {
            ...prev,
            output,
          };
        });
      }
    }

    if (isPlainObject(transformData) && !isArray(transformData)) {
      this.handleUpdatePreviewData(transformData);
      return;
    }
    // split 可能返回为一个数组
    if (isArray(transformData)) {
      transformData.forEach(item => {
        this.handleUpdatePreviewData(item);
      });
    }
  }
}
